import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Lock, Heart, Eye, EyeOff } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

/**
 * Soft, optional "Save Your Access" block shown on the LTL recipient view.
 *
 * - Always optional. Never blocks playback. Submit is fully client-side for
 *   now (no Supabase write) so this is safe to ship without a migration.
 * - The submit handler is a no-op that simply transitions to a calm thank-you
 *   state, so it cannot break, error or affect any other recipient flow.
 * - When a backend table/field is provisioned, this is the only spot that
 *   needs to be wired up (the markup, validation and copy stay the same).
 */
const RecipientSaveAccess = ({ qrCode }) => {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | submitting | saved | dismissed
  const [touched, setTouched] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSave = async (e) => {
    e.preventDefault();
    if (phase === 'submitting') return;
    if (!valid) {
      setTouched(true);
      return;
    }
    setErrorMsg('');
    setPhase('submitting');
    try {
      const res = await fetch(
        `${API_BASE}/api/memory/${encodeURIComponent(qrCode)}/save-access`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        },
      );
      if (!res.ok) {
        setErrorMsg('We couldn’t save this just now. Please try again.');
        setPhase('idle');
        return;
      }
      setPhase('saved');
    } catch {
      setErrorMsg('We couldn’t save this just now. Please try again.');
      setPhase('idle');
    }
  };

  if (phase === 'dismissed') return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 1 }}
      className="mt-20"
      data-testid="ltl-save-access-section"
      aria-labelledby="ltl-save-access-heading"
    >
      <div className="w-8 h-px bg-[#C6A96B]/30 mx-auto mb-10" aria-hidden="true" />

      <div
        className="mx-auto px-7 sm:px-10 py-10 sm:py-12 max-w-md"
        style={{
          background: '#FBF8F1',
          borderRadius: '14px',
          boxShadow: '0 18px 44px rgba(11, 31, 58, 0.06)',
        }}
      >
        {phase === 'saved' ? (
          <div className="text-center" data-testid="ltl-save-access-confirmation">
            <p
              className="text-[10px] tracking-[0.36em] uppercase mb-4"
              style={{ color: '#C6A96B', fontWeight: 500 }}
            >
              Your access is saved
            </p>
            <h3
              id="ltl-save-access-heading"
              className="text-2xl sm:text-3xl font-light leading-snug mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0B1F3A' }}
            >
              Kept gently for you.
            </h3>
            <p
              className="text-[15px] leading-relaxed italic"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#8A7F73' }}
            >
              We&rsquo;ll let you know if anything changes &mdash; quietly, only when it matters.
            </p>
          </div>
        ) : (
          <>
            <p
              className="text-[10px] tracking-[0.36em] uppercase text-center mb-4"
              style={{ color: '#C6A96B', fontWeight: 500 }}
              data-testid="ltl-save-access-eyebrow"
            >
              Optional
            </p>
            <h3
              id="ltl-save-access-heading"
              className="text-2xl sm:text-3xl font-light leading-snug text-center mb-5"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0B1F3A' }}
              data-testid="ltl-save-access-headline"
            >
              Keep this close
            </h3>
            <p
              className="text-[15px] leading-[1.85] text-center mb-8 mx-auto"
              style={{ fontFamily: 'Georgia, serif', color: '#8A7F73', maxWidth: '32em' }}
              data-testid="ltl-save-access-body"
            >
              This memory is yours to return to, whenever you choose.
              <br />
              If you&rsquo;d like to keep access, receive gentle updates, or extend storage in the future, you can save your details below.
            </p>

            <form onSubmit={handleSave} noValidate>
              <label htmlFor="ltl-save-access-email" className="sr-only">
                Email address
              </label>
              <input
                id="ltl-save-access-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (touched) setTouched(false); }}
                placeholder="Enter your email (optional)"
                aria-invalid={touched && !valid}
                data-testid="ltl-save-access-email-input"
                className="w-full bg-white outline-none px-5 py-4 text-[15px] transition-colors duration-300 focus:ring-2 focus:ring-[#C6A96B]/35"
                style={{
                  border: '1px solid rgba(198, 169, 107, 0.18)',
                  borderRadius: '10px',
                  color: '#0B1F3A',
                  fontFamily: 'Georgia, serif',
                }}
              />
              {touched && !valid && email.trim().length > 0 && (
                <p
                  className="text-xs italic mt-2 text-center"
                  style={{ color: '#A88560', fontFamily: "'Cormorant Garamond', serif" }}
                  data-testid="ltl-save-access-error"
                >
                  Please enter a valid email so we can keep your access safe.
                </p>
              )}
              {errorMsg && (
                <p
                  className="text-xs italic mt-2 text-center"
                  style={{ color: '#A88560', fontFamily: "'Cormorant Garamond', serif" }}
                  data-testid="ltl-save-access-server-error"
                  role="alert"
                >
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                data-testid="ltl-save-access-submit"
                disabled={phase === 'submitting'}
                className="w-full mt-6 tracking-[0.22em] uppercase text-xs py-4 transition-all duration-300 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: '#0B1F3A',
                  color: '#F5E9CC',
                  borderRadius: '999px',
                  fontWeight: 500,
                  letterSpacing: '0.22em',
                  boxShadow: '0 6px 18px rgba(11, 31, 58, 0.18)',
                }}
              >
                {phase === 'submitting' ? 'Saving…' : 'Save my access'}
              </button>

              <button
                type="button"
                onClick={() => setPhase('dismissed')}
                disabled={phase === 'submitting'}
                data-testid="ltl-save-access-skip"
                className="block mx-auto mt-5 text-[12px] italic transition-opacity duration-300 hover:opacity-70 disabled:opacity-50"
                style={{
                  color: '#8A7F73',
                  fontFamily: "'Cormorant Garamond', serif",
                  background: 'transparent',
                  border: 0,
                  textUnderlineOffset: '3px',
                  textDecoration: 'underline',
                }}
              >
                Continue without saving
              </button>
            </form>
          </>
        )}
      </div>
    </motion.section>
  );
};

/**
 * The recipient-facing memory viewer.
 *
 * Flow:
 *   1. Fetch `/api/memory/{qr}/meta` for safe metadata (has_password, hint, recipient name).
 *   2. If no password → auto-unlock and render the memory.
 *   3. If password is set → show a gentle password screen. On submit,
 *      POST `/api/memory/{qr}/unlock` to retrieve the memory content.
 */
const MemoryViewer = ({ qrCode }) => {
  const [phase, setPhase] = useState('loading'); // loading | gate | memory | error | notfound
  const [meta, setMeta] = useState(null);
  const [memory, setMemory] = useState(null);
  const [error, setError] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wrongCount, setWrongCount] = useState(0);

  const autoUnlock = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qrCode)}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        setError('We couldn’t open this memory right now. Please try again soon.');
        setPhase('error');
        return;
      }
      const data = await res.json();
      setMemory(data);
      setPhase('memory');
    } catch {
      setError('Connection failed. Please check your internet and try again.');
      setPhase('error');
    }
  }, [qrCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qrCode)}/meta`);
        if (cancelled) return;
        if (res.status === 404) { setPhase('notfound'); return; }
        if (!res.ok) {
          setError('We couldn’t reach this memory. Please try again soon.');
          setPhase('error');
          return;
        }
        const data = await res.json();
        setMeta(data);
        if (!data.has_memory) { setPhase('notfound'); return; }
        if (data.has_password) {
          setPhase('gate');
        } else {
          await autoUnlock();
        }
      } catch {
        if (!cancelled) {
          setError('Connection failed. Please check your internet and try again.');
          setPhase('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [qrCode, autoUnlock]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qrCode)}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      if (res.status === 401) {
        setWrongCount((c) => c + 1);
        setSubmitting(false);
        return;
      }
      if (!res.ok) {
        setError('Something went wrong. Please try again.');
        setPhase('error');
        return;
      }
      const data = await res.json();
      setMemory(data);
      setPhase('memory');
    } catch {
      setError('Connection failed. Please check your internet and try again.');
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center" data-testid="memory-viewer-loading">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <Loader2 className="mx-auto mb-4 text-[#C6A96B] animate-spin" size={32} strokeWidth={1.5} />
          <p className="text-sm tracking-[0.15em] uppercase text-[#6B6259]">Opening this memory</p>
        </motion.div>
      </div>
    );
  }

  if (phase === 'notfound') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6" data-testid="memory-viewer-notfound">
        <motion.div {...fadeUp} className="max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-6">
            <Heart className="text-[#C6A96B]" size={20} strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] mb-4 font-light">Nothing here yet</h1>
          <p className="text-[#6B6259] leading-relaxed text-sm sm:text-base">This memory hasn’t been saved yet. Please check back soon.</p>
        </motion.div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6" data-testid="memory-viewer-error">
        <motion.div {...fadeUp} className="max-w-md text-center">
          <h1 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] mb-4 font-light">We can’t open this right now</h1>
          <p className="text-[#6B6259] leading-relaxed text-sm sm:text-base">{error || 'Please try again in a moment.'}</p>
        </motion.div>
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6 py-20" data-testid="memory-password-gate">
        <motion.div {...fadeUp} className="max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-8">
            <Lock className="text-[#C6A96B]" size={22} strokeWidth={1.5} />
          </div>
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#C6A96B] mb-4 font-medium">Sent2ment</p>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] mb-4 font-light leading-[1.25]" data-testid="memory-gate-heading">
            This message was protected with care
          </h1>
          <p className="text-[#6B6259] leading-relaxed text-sm sm:text-base mb-8 px-2">
            A moment has been kept safe for you.<br />
            Please enter the password to continue.
          </p>

          <form onSubmit={handleUnlock} className="text-left space-y-4">
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoFocus
                autoComplete="current-password"
                placeholder="Password"
                data-testid="memory-gate-password-input"
                className="w-full bg-white border border-[#C6A96B]/25 focus:border-[#C6A96B]/60 outline-none px-4 py-3 pr-10 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300 text-center tracking-[0.15em]"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Hide password' : 'Show password'}
                className="absolute inset-y-0 right-0 px-3 text-[#6B6259] hover:text-[#0B1F3A] transition-colors"
              >
                {showPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
              </button>
            </div>

            {wrongCount > 0 && (
              <p className="text-xs text-red-400 text-center" data-testid="memory-gate-wrong">
                That isn’t quite right. Please try again.
              </p>
            )}

            {meta?.password_hint && (
              <p className="text-xs text-[#6B6259]/80 italic text-center leading-relaxed" data-testid="memory-gate-hint">
                A gentle reminder: {meta.password_hint}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={!pwd || submitting}
              whileHover={(!pwd || submitting) ? {} : { scale: 1.01 }}
              whileTap={(!pwd || submitting) ? {} : { scale: 0.99 }}
              data-testid="memory-gate-submit-btn"
              className="w-full bg-[#C6A96B] text-white font-medium tracking-[0.2em] uppercase text-xs py-4 transition-all duration-300 hover:bg-[#b89a5e] disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={14} /> Opening…</span>
              ) : (
                'Continue'
              )}
            </motion.button>
            <p className="text-center mt-3">
              <a
                href={`/vault-passcode-reset/${encodeURIComponent(qrCode)}`}
                data-testid="memory-gate-forgot-link"
                className="text-[11px] italic underline"
                style={{ color: 'rgba(107, 98, 89, 0.8)', fontFamily: 'Georgia, serif' }}
              >
                Forgot your passcode?
              </a>
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  // phase === 'memory'
  const { recipient_name, title, audio_url, video_url, image_urls = [], message_text } = memory || {};
  const hasVoice = !!audio_url;
  const hasVideo = !!video_url;
  const hasPhotos = Array.isArray(image_urls) && image_urls.length > 0;
  // Prefer the sender-supplied title; fall back to the recipient's name.
  // If we have neither, we show nothing (never a placeholder like "Your memory").
  const displayHeading = (title && title.trim()) || (recipient_name && recipient_name.trim()) || '';
  const hasAnyMedia = hasVoice || hasVideo || hasPhotos || !!message_text;

  return (
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="memory-viewer">
      <div className="max-w-xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
        <motion.div {...fadeUp}>

          {/* ── Intro ── */}
          <div className="text-center mb-16 sm:mb-20">
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#C6A96B]/90 mb-6 font-medium">
              A message for you
            </p>
            {displayHeading && (
              <h1
                className="font-heading text-[2rem] leading-[1.2] sm:text-[2.5rem] sm:leading-[1.15] text-[#0B1F3A] font-light mb-6"
                data-testid="memory-viewer-heading"
              >
                {displayHeading}
              </h1>
            )}
            <div className="w-8 h-px bg-[#C6A96B]/35 mx-auto" />
            {hasAnyMedia && (
              <p className="font-heading italic text-[#6B6259]/80 text-sm sm:text-base mt-8 leading-relaxed px-4">
                Left exactly as it was shared, for you to return to.
              </p>
            )}
          </div>

          {/* ── Media ── */}
          <AnimatePresence>
            {hasVoice && (
              <motion.div
                key="voice"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="mb-14"
                data-testid="memory-view-voice"
              >
                <p className="font-heading italic text-[#6B6259] text-base sm:text-lg mb-6 text-center">
                  A voice, kept for you
                </p>
                <div className="px-2 sm:px-0">
                  <audio
                    controls
                    preload="metadata"
                    src={audio_url}
                    className="w-full"
                    data-testid="memory-view-audio"
                  />
                </div>
              </motion.div>
            )}

            {hasVideo && (
              <motion.div
                key="video"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="mb-14"
                data-testid="memory-view-video-wrap"
              >
                <p className="font-heading italic text-[#6B6259] text-base sm:text-lg mb-6 text-center">
                  A moment captured
                </p>
                <video
                  controls
                  preload="metadata"
                  src={video_url}
                  playsInline
                  className="w-full bg-[#0B1F3A]"
                  data-testid="memory-view-video"
                />
              </motion.div>
            )}

            {hasPhotos && (
              <motion.div
                key="photos"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="mb-14"
                data-testid="memory-view-photos"
              >
                <p className="font-heading italic text-[#6B6259] text-base sm:text-lg mb-6 text-center">
                  Moments to hold onto
                </p>
                <div className="space-y-6">
                  {image_urls.map((url, i) => (
                    <img
                      key={url + i}
                      src={url}
                      alt=""
                      loading="lazy"
                      className="w-full object-contain"
                      data-testid={`memory-view-photo-${i}`}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {message_text && (
              <motion.div
                key="msg"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="mb-14"
                data-testid="memory-view-message"
              >
                <p className="font-heading text-xl sm:text-2xl text-[#0B1F3A] font-light leading-[1.7] italic text-center px-2">
                  “{message_text}”
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {!hasAnyMedia && (
            <p className="font-heading italic text-center text-base text-[#6B6259]/80" data-testid="memory-view-empty">
              This moment is being prepared. Please return again soon.
            </p>
          )}

          {/* Memory Vault link — surfaces only when a vault has been prepared for this QR */}
          {meta?.vault_enabled && meta?.vault_has_content && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 1.2 }}
              className="text-center mt-16"
              data-testid="memory-view-vault-link-block"
            >
              <div className="w-8 h-px bg-[#C6A96B]/35 mx-auto mb-6" />
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#C6A96B] mb-4 font-medium">There's more, kept for you</p>
              <h3 className="font-heading text-xl sm:text-2xl text-[#0B1F3A] font-light leading-snug mb-3 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                A private Memory Vault
              </h3>
              <p className="text-[#6B6259] text-sm leading-relaxed mb-6 px-4 max-w-sm mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
                A space gently prepared for you — with quiet messages and moments to return to whenever you need them.
              </p>
              <a
                href={`/vault/${encodeURIComponent(qrCode)}`}
                data-testid="memory-view-vault-link"
                className="inline-block bg-[#C6A96B] text-white font-medium tracking-[0.22em] uppercase text-xs px-10 py-4 transition-all duration-300 hover:opacity-90"
              >
                Open the Memory Vault
              </a>
            </motion.div>
          )}

          {/* ── Save Your Access ── soft, optional. Visually consistent with
                LTL recipient styling (cream body, midnight text, warm taupe
                secondary). Sits BELOW the playback / vault link and ABOVE
                the footer mark per spec. Front-end only for now: submit
                renders a calm thank-you state locally and does NOT persist
                to Supabase yet (no schema available). Recipient access to
                the memory is never gated by this section. */}
          <RecipientSaveAccess qrCode={qrCode} />

          {/* ── Footer mark ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1.2 }}
            className="text-center mt-20"
          >
            <div className="w-8 h-px bg-[#C6A96B]/30 mx-auto mb-5" />
            <div className="inline-flex items-center gap-2 text-[#C6A96B]/80">
              <Heart size={13} strokeWidth={1.5} />
              <p className="text-[10px] tracking-[0.4em] uppercase">Kept forever</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default MemoryViewer;
